import { DownOutlined, FileExcelOutlined, FilePdfOutlined, PrinterOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Dropdown, Menu, Segmented, Spin, Table, Tooltip } from "antd";
import jsPDF from "jspdf";
import "jspdf-autotable";
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { getCustomerSummary, getSupplierSummary } from "./account.api";
import "./account.css";

const TAB_CUSTOMER = "customer";
const TAB_SUPPLIER = "supplier";

const formatAmount = (value) => {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const CustomerSupplierSummaryReport = () => {
  const [activeTab, setActiveTab] = useState(TAB_CUSTOMER);
  const [customerData, setCustomerData] = useState(null);
  const [supplierData, setSupplierData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [custRes, supRes] = await Promise.all([getCustomerSummary(), getSupplierSummary()]);
        setCustomerData(custRes?.list || []);
        setSupplierData(supRes?.list || []);
      } catch (err) {
        setError(err.response?.data?.error || err.message || "Failed to load summary");
        setCustomerData([]);
        setSupplierData([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const customerList = customerData || [];
  const supplierList = supplierData || [];
  const totalReceived = customerList.reduce((s, r) => s + Number(r.total_received || 0), 0);
  const totalCustomerBilled = customerList.reduce((s, r) => s + Number(r.total_billed || 0), 0);
  const totalCustomerDue = customerList.reduce((s, r) => s + Number(r.total_due || 0), 0);
  const totalPaid = supplierList.reduce((s, r) => s + Number(r.total_paid || 0), 0);
  const totalSupplierBilled = supplierList.reduce((s, r) => s + Number(r.total_billed || 0), 0);
  const totalSupplierDue = supplierList.reduce((s, r) => s + Number(r.total_due || 0), 0);

  const exportCustomerPDF = () => {
    const headers = ["Customer", "Net billed (₹)", "Total Received (₹)", "Receivable (₹)", "Payable (₹)"];
    const rows = customerList.map((r) => [
      r.name || "—",
      formatAmount(r.total_billed),
      formatAmount(r.total_received),
      formatAmount(r.total_due),
      formatAmount(0),
    ]);
    if (rows.length > 0) rows.push(["TOTAL", formatAmount(totalCustomerBilled), formatAmount(totalReceived), formatAmount(totalCustomerDue), formatAmount(0)]);
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFontSize(14);
    doc.text("Customer Summary – Money Received", 40, 25);
    doc.autoTable({ head: [headers], body: rows, startY: 35, theme: "grid", headStyles: { fillColor: [0, 0, 0], fontSize: 9, textColor: [255, 255, 255] }, bodyStyles: { fontSize: 8 } });
    doc.save("customer-summary.pdf");
  };

  const exportSupplierPDF = () => {
    const headers = ["Supplier", "Net billed (₹)", "Total Paid (₹)", "Receivable (₹)", "Payable (₹)"];
    const rows = supplierList.map((r) => [
      r.name || "—",
      formatAmount(r.total_billed),
      formatAmount(r.total_paid),
      formatAmount(0),
      formatAmount(r.total_due),
    ]);
    if (rows.length > 0) rows.push(["TOTAL", formatAmount(totalSupplierBilled), formatAmount(totalPaid), formatAmount(0), formatAmount(totalSupplierDue)]);
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    doc.setFontSize(14);
    doc.text("Supplier Summary – Money Paid", 40, 25);
    doc.autoTable({ head: [headers], body: rows, startY: 35, theme: "grid", headStyles: { fillColor: [0, 0, 0], fontSize: 9, textColor: [255, 255, 255] }, bodyStyles: { fontSize: 8 } });
    doc.save("supplier-summary.pdf");
  };

  const exportCustomerExcel = () => {
    const headers = ["Customer", "Net billed (₹)", "Total Received (₹)", "Receivable (₹)", "Payable (₹)"];
    const rows = customerList.map((r) => [r.name || "—", r.total_billed ?? 0, r.total_received ?? 0, r.total_due ?? 0, 0]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Customer Summary");
    XLSX.writeFile(wb, "customer-summary.xlsx");
  };

  const exportSupplierExcel = () => {
    const headers = ["Supplier", "Net billed (₹)", "Total Paid (₹)", "Receivable (₹)", "Payable (₹)"];
    const rows = supplierList.map((r) => [r.name || "—", r.total_billed ?? 0, r.total_paid ?? 0, 0, r.total_due ?? 0]);
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Supplier Summary");
    XLSX.writeFile(wb, "supplier-summary.xlsx");
  };

  const customerColumns = [
    { title: "S.No", key: "sno", width: 70, render: (_, __, i) => i + 1 },
    {
      title: "Customer",
      dataIndex: "name",
      key: "name",
      width: 260,
      render: (v) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.25 }}>{v || "—"}</div>
      ),
    },
    {
      title: (
        <Tooltip title="After product-line % discounts, bill discount, and round-off (same as invoice net total).">
          <span>Net billed (₹)</span>
        </Tooltip>
      ),
      dataIndex: "total_billed",
      key: "total_billed",
      align: "right",
      width: 140,
      render: (v) => formatAmount(v),
    },
    { title: "Total Received (₹)", dataIndex: "total_received", key: "total_received", align: "right", width: 150, render: (v) => formatAmount(v) },
    { title: "Receivable (₹)", dataIndex: "total_due", key: "receivable", align: "right", width: 130, render: (v) => formatAmount(v) },
    { title: "Payable (₹)", key: "payable", align: "right", width: 120, render: () => formatAmount(0) },
  ];

  const supplierColumns = [
    { title: "S.No", key: "sno", width: 70, render: (_, __, i) => i + 1 },
    {
      title: "Supplier",
      dataIndex: "name",
      key: "name",
      width: 260,
      render: (v) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.25 }}>{v || "—"}</div>
      ),
    },
    {
      title: (
        <Tooltip title="After line-item discounts and bill-level discount (net purchase total).">
          <span>Net billed (₹)</span>
        </Tooltip>
      ),
      dataIndex: "total_billed",
      key: "total_billed",
      align: "right",
      width: 140,
      render: (v) => formatAmount(v),
    },
    { title: "Total Paid (₹)", dataIndex: "total_paid", key: "total_paid", align: "right", width: 130, render: (v) => formatAmount(v) },
    { title: "Receivable (₹)", key: "receivable", align: "right", width: 120, render: () => formatAmount(0) },
    { title: "Payable (₹)", dataIndex: "total_due", key: "payable", align: "right", width: 130, render: (v) => formatAmount(v) },
  ];


  if (loading) {
    return (
      <div className="pwpl-page">
        <Card className="pwpl-card">
          <Spin size="large" style={{ display: "block", textAlign: "center", padding: "50px" }} />
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pwpl-page">
        <Card className="pwpl-card">
          <Alert message="Error" description={error} type="error" showIcon />
        </Card>
      </div>
    );
  }

  return (
    <div className="pwpl-page">
      <Card className="pwpl-card pwpl-card-modern" bordered={false}>
        <div className="pwpl-header erp-no-print">
          <div className="pwpl-header-left">
            <h1 className="pwpl-main-title">Customer & Supplier Summary</h1>
            <Segmented
              className="pwpl-segmented"
              options={[
                { label: "Customer Summary (Money Received)", value: TAB_CUSTOMER },
                { label: "Supplier Summary (Money Paid)", value: TAB_SUPPLIER },
              ]}
              value={activeTab}
              onChange={setActiveTab}
            />
          </div>
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item key="pdf" icon={<FilePdfOutlined />} onClick={() => (activeTab === TAB_CUSTOMER ? exportCustomerPDF() : exportSupplierPDF())}>
                  Download PDF
                </Menu.Item>
                <Menu.Item key="excel" icon={<FileExcelOutlined />} onClick={() => (activeTab === TAB_CUSTOMER ? exportCustomerExcel() : exportSupplierExcel())}>
                  Download Excel
                </Menu.Item>
                <Menu.Item key="print" icon={<PrinterOutlined />} onClick={() => window.print()}>
                  Print
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="primary" size="small" className="pwpl-export-btn" style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}>
              Download / Print <DownOutlined />
            </Button>
          </Dropdown>
        </div>

        {activeTab === TAB_CUSTOMER && (
          <div className="erp-no-print">
            <div className="pwpl-summary-cards">
              <div className="pwpl-summary-card">
                <span className="pwpl-summary-label">Total Received</span>
                <span className="pwpl-summary-value">₹{formatAmount(totalReceived)}</span>
              </div>
              <div className="pwpl-summary-card">
                <Tooltip title="Sum of invoice amounts after all discounts.">
                  <span className="pwpl-summary-label">Net billed</span>
                </Tooltip>
                <span className="pwpl-summary-value">₹{formatAmount(totalCustomerBilled)}</span>
              </div>
              <div className="pwpl-summary-card">
                <span className="pwpl-summary-label">Total Receivable</span>
                <span className="pwpl-summary-value">₹{formatAmount(totalCustomerDue)}</span>
              </div>
              <div className="pwpl-summary-card">
                <span className="pwpl-summary-label">Total Payable</span>
                <span className="pwpl-summary-value">₹{formatAmount(0)}</span>
              </div>
              <div className="pwpl-summary-card">
                <span className="pwpl-summary-label">Customers</span>
                <span className="pwpl-summary-value">{customerList.length}</span>
              </div>
            </div>
            <div className="pwpl-table-wrap">
              <Table
                columns={customerColumns}
                dataSource={customerList.map((r, i) => ({ ...r, key: r.id ?? `c-${i}` }))}
                pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"] }}
                scroll={{ x: true }}
                size="small"
              />
            </div>
            <div className="pwpl-footer">
              <span className="pwpl-summary">
                {customerList.length} customer(s) | Receivable: ₹{formatAmount(totalCustomerDue)} | Payable: ₹{formatAmount(0)}
              </span>
            </div>
          </div>
        )}

        {activeTab === TAB_SUPPLIER && (
          <div className="erp-no-print">
            <div className="pwpl-summary-cards">
              <div className="pwpl-summary-card">
                <span className="pwpl-summary-label">Total Paid</span>
                <span className="pwpl-summary-value">₹{formatAmount(totalPaid)}</span>
              </div>
              <div className="pwpl-summary-card">
                <Tooltip title="Sum of purchase amounts after bill discount.">
                  <span className="pwpl-summary-label">Net billed</span>
                </Tooltip>
                <span className="pwpl-summary-value">₹{formatAmount(totalSupplierBilled)}</span>
              </div>
              <div className="pwpl-summary-card">
                <span className="pwpl-summary-label">Total Receivable</span>
                <span className="pwpl-summary-value">₹{formatAmount(0)}</span>
              </div>
              <div className="pwpl-summary-card">
                <span className="pwpl-summary-label">Total Payable</span>
                <span className="pwpl-summary-value">₹{formatAmount(totalSupplierDue)}</span>
              </div>
              <div className="pwpl-summary-card">
                <span className="pwpl-summary-label">Suppliers</span>
                <span className="pwpl-summary-value">{supplierList.length}</span>
              </div>
            </div>
            <div className="pwpl-table-wrap">
              <Table
                columns={supplierColumns}
                dataSource={supplierList.map((r, i) => ({ ...r, key: r.id ?? `s-${i}` }))}
                pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"] }}
                scroll={{ x: true }}
                size="small"
              />
            </div>
            <div className="pwpl-footer">
              <span className="pwpl-summary">
                {supplierList.length} supplier(s) | Receivable: ₹{formatAmount(0)} | Payable: ₹{formatAmount(totalSupplierDue)}
              </span>
            </div>
          </div>
        )}

        {/* Print-only: match download heading + show only table data */}
        <div className="cps-print-only cps-print-tables" aria-hidden="true">
          {activeTab === TAB_CUSTOMER ? (
            <>
              <div className="cps-print-title">Customer Summary – Money Received</div>
              <div className="cps-print-table-wrap">
                <table className="pwpl-table">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Customer</th>
                      <th className="pwpl-th-amount">Net billed (₹)</th>
                      <th className="pwpl-th-amount">Total Received (₹)</th>
                      <th className="pwpl-th-amount">Receivable (₹)</th>
                      <th className="pwpl-th-amount">Payable (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerList.length > 0 ? (
                      <>
                        {customerList.map((r, i) => (
                          <tr key={r.id ?? `c-p-${i}`}>
                            <td>{i + 1}</td>
                            <td className="pwpl-name">{r.name || "—"}</td>
                            <td className="pwpl-amount">{formatAmount(r.total_billed)}</td>
                            <td className="pwpl-amount">{formatAmount(r.total_received)}</td>
                            <td className="pwpl-amount">{formatAmount(r.total_due)}</td>
                            <td className="pwpl-amount">{formatAmount(0)}</td>
                          </tr>
                        ))}
                        <tr className="pwpl-total-row">
                          <td colSpan={2}><strong>TOTAL</strong></td>
                          <td className="pwpl-amount"><strong>{formatAmount(totalCustomerBilled)}</strong></td>
                          <td className="pwpl-amount"><strong>{formatAmount(totalReceived)}</strong></td>
                          <td className="pwpl-amount"><strong>{formatAmount(totalCustomerDue)}</strong></td>
                          <td className="pwpl-amount"><strong>{formatAmount(0)}</strong></td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan={6}>No customer data</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="cps-print-title">Supplier Summary – Money Paid</div>
              <div className="cps-print-table-wrap">
                <table className="pwpl-table">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Supplier</th>
                      <th className="pwpl-th-amount">Net billed (₹)</th>
                      <th className="pwpl-th-amount">Total Paid (₹)</th>
                      <th className="pwpl-th-amount">Receivable (₹)</th>
                      <th className="pwpl-th-amount">Payable (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {supplierList.length > 0 ? (
                      <>
                        {supplierList.map((r, i) => (
                          <tr key={r.id ?? `s-p-${i}`}>
                            <td>{i + 1}</td>
                            <td className="pwpl-name">{r.name || "—"}</td>
                            <td className="pwpl-amount">{formatAmount(r.total_billed)}</td>
                            <td className="pwpl-amount">{formatAmount(r.total_paid)}</td>
                            <td className="pwpl-amount">{formatAmount(0)}</td>
                            <td className="pwpl-amount">{formatAmount(r.total_due)}</td>
                          </tr>
                        ))}
                        <tr className="pwpl-total-row">
                          <td colSpan={2}><strong>TOTAL</strong></td>
                          <td className="pwpl-amount"><strong>{formatAmount(totalSupplierBilled)}</strong></td>
                          <td className="pwpl-amount"><strong>{formatAmount(totalPaid)}</strong></td>
                          <td className="pwpl-amount"><strong>{formatAmount(0)}</strong></td>
                          <td className="pwpl-amount"><strong>{formatAmount(totalSupplierDue)}</strong></td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan={6}>No supplier data</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CustomerSupplierSummaryReport;
